import { Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import { Fixture, FixtureType, Game, Option, OptionMarket, Participant, ParticipantOption, ParticipantPriceStatus, Result } from '@cds/betting-offer';
import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { PicksView } from '@cds/betting-offer/domain-specific';
import { Tv1PickData, Tv2PickData } from '@cds/query-objects';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { WinGameParticipant, toWinGameFixture } from '@frontend/sports/racing/feature/models';
import { Store } from '@ngrx/store';
import { OptionQuery, OptionRequest } from 'packages/sports/web/app/src/navigation-core/pick-uri.model';

import { IBetslipRootState } from '../../../base/store/state';
import { PickAddPayload } from '../../../core/external-betslip-actions';
import { BetBuilderPickId, V1PickId, V2OptionMarketPickId, V2WinPickId } from '../../../core/picks/pick-id';
import AffiliateActions from '../state/affiliate.actions';

interface PickProcessorOption {
    id: number | string;
    fixture: Fixture;
    source: string;
    preCreatedFixture?: Fixture;
    optionGroupId?: number;
    priceId?: number;
    tv1Legs?: Tv1PickData[];
    tv2Legs?: Tv2PickData[];
}

interface PickProcessor {
    process(pickOption: PickProcessorOption): void;
    canProcess(fixture: Fixture): boolean;
}

abstract class BasePickProcessor implements PickProcessor {
    constructor(protected store: Store<IBetslipRootState>) {}

    abstract canProcess(fixture: Fixture): boolean;
    abstract addSinglePick(pickOption: PickProcessorOption): void;
    abstract addGroupPick(pickOption: PickProcessorOption): void;

    process(pickOption: PickProcessorOption): void {
        if (pickOption.tv1Legs?.length || pickOption.tv2Legs?.length) {
            this.addGroupPick(pickOption);
        } else {
            this.addSinglePick(pickOption);
        }
    }
}

class MarketPickProcessor extends BasePickProcessor {
    constructor(
        store: Store<IBetslipRootState>,
        private factory: FixtureFactory,
    ) {
        super(store);
    }

    addSinglePick(pickOption: PickProcessorOption): void {
        const event = this.factory.create(pickOption.fixture);
        const optionGroup = event.optionGroups.find((og) => og.options.some((o) => o.id === pickOption.id));

        if (optionGroup) {
            const option = optionGroup.options.find((o) => o.id === pickOption.id)!;
            const priceId = pickOption.priceId === option.boostedPriceId ? option.boostedPriceId : option.priceId;
            const isPriceBoosted = option.boostedPriceId ? option.boostedPriceId === priceId : false;
            const pickId =
                event.offerSource === OfferSource.V1
                    ? new V1PickId(event.id, parseInt(optionGroup.id), option.id)
                    : new V2OptionMarketPickId(event.id, parseInt(optionGroup.id), option.id, priceId!);

            this.store.dispatch(
                AffiliateActions.addAffiliatePick({
                    payload: {
                        pickId,
                        tracking: { source: pickOption.source, affiliate: true },
                        isPriceBoosted,
                    },
                }),
            );
        }
    }

    addGroupPick(pickOption: PickProcessorOption): void {
        const event = this.factory.create(pickOption.fixture);

        if (event.betBuilderInfo) {
            const tv1Picks =
                pickOption.tv1Legs?.map((leg) => ({
                    pickId: new V1PickId(event.id, leg.gameId!, leg.resultId!),
                    tracking: { source: pickOption.source, affiliate: true },
                    priceId: pickOption.priceId,
                    groupInfo: { groupId: leg.pickGroupId!, provider: event.betBuilderInfo!.betBuilderProvider },
                })) || [];

            const tv2Picks =
                pickOption.tv2Legs?.map((leg) => ({
                    pickId: new V2OptionMarketPickId(
                        event.hybridFixtureData?.fixtureId ?? event.id,
                        leg.optionMarketId!,
                        leg.optionId!,
                        pickOption.priceId!,
                    ),
                    tracking: { source: pickOption.source, affiliate: true },
                    priceId: pickOption.priceId,
                    groupInfo: { groupId: leg.pickGroupId!, provider: event.betBuilderInfo!.betBuilderProvider },
                })) || [];

            if (!tv1Picks.length && !tv2Picks.length) {
                throw new Error(`No pick group details found for eventId: ${event.id}`);
            }

            let picks: PickAddPayload[];

            if (tv1Picks.length && tv2Picks.length) {
                picks = [...tv1Picks, ...tv2Picks];
            } else {
                picks = tv1Picks.length ? tv1Picks : tv2Picks;
            }

            this.store.dispatch(AffiliateActions.addAffiliateGroupPick({ picks }));
        }
    }

    canProcess(fixture: Fixture): boolean {
        return fixture.fixtureType !== FixtureType.Antepost && fixture.fixtureType !== FixtureType.DayOfRace;
    }
}

class ParticipantMarketPickProcessor extends BasePickProcessor {
    constructor(store: Store<IBetslipRootState>) {
        super(store);
    }

    addSinglePick(pickOption: PickProcessorOption): void {
        const winGameFixture = toWinGameFixture(pickOption.fixture);
        const participant = winGameFixture.participants.find((p) => p.id === pickOption.id);

        if (participant && pickOption.optionGroupId) {
            const [market, order] = this.getMarketAndIndex(participant, pickOption.optionGroupId);

            if (market?.id && order) {
                this.store.dispatch(
                    AffiliateActions.addAffiliatePick({
                        payload: {
                            pickId: new V2WinPickId(winGameFixture.id, market.marketId, participant.id),
                            tracking: { source: pickOption.source, affiliate: true },
                            priceId: market.price.id,
                        },
                    }),
                );
            }
        }
    }

    addGroupPick(pickOption: PickProcessorOption): void {
        throw new Error('Participant Group Picks are not supported yet...');
    }

    canProcess(fixture: Fixture): boolean {
        return fixture.fixtureType === FixtureType.Antepost || fixture.fixtureType === FixtureType.DayOfRace;
    }

    private getMarketAndIndex(participant: WinGameParticipant, optionGroupId: number): [ParticipantOption?, number?] {
        const markets = participant.options.filter((o: ParticipantOption) => o.marketId === optionGroupId);

        if (!markets.length) {
            return [undefined, undefined];
        }

        const market = markets.find((m: ParticipantOption) => m.participantPriceStatus === ParticipantPriceStatus.Visible) || markets[0];
        const index = participant.options.indexOf(market);

        return [market, index + 1];
    }
}

class BetBuilderPickProcessor extends BasePickProcessor {
    constructor(
        store: Store<IBetslipRootState>,
        private factory: FixtureFactory,
    ) {
        super(store);
    }

    canProcess(fixture: Fixture | undefined): boolean {
        return fixture?.source === OfferSource.BetBuilder;
    }

    addSinglePick(pickOption: PickProcessorOption): void {
        const event = this.factory.create(pickOption.fixture);
        const market = pickOption.preCreatedFixture?.optionMarkets.find((optMr: OptionMarket) =>
            optMr.options.some((opt: Option) => opt.id === pickOption.id),
        );
        if (market && pickOption.preCreatedFixture) {
            const pickId = new BetBuilderPickId(event.id, pickOption.preCreatedFixture.id, market.id, +pickOption.id, pickOption.priceId!);
            this.store.dispatch(
                AffiliateActions.addAffiliateBetBuilderPick({
                    pickId,
                    tracking: { source: pickOption.source, affiliate: true },
                    eventData: {
                        event: {
                            id: event.id,
                            date: new Date(event.startDate),
                            name: event.name,
                            isLive: event.live,
                            groupId: event.groupId,
                        },
                        league: {
                            id: event.league.id,
                            name: event.league.name,
                            parentLeagueId: event.league.parentLeagueId,
                        },
                        region: {
                            id: event.region.id,
                            name: event.region.name,
                        },
                        offerSource: event.offerSource,
                        betBuilderTradingV2FixtureId: event.betBuilderTradingV2FixtureId,
                    },
                }),
            );
        }
    }

    addGroupPick(pickOption: PickProcessorOption): void {
        throw new Error('PreCreated BetBuilder Group Picks are not supported...');
    }
}

@Injectable()
export class AffiliateProcessorService {
    private processors: PickProcessor[];

    constructor(
        private store: Store<IBetslipRootState>,
        factory: FixtureFactory,
    ) {
        this.processors = [
            new BetBuilderPickProcessor(store, factory),
            new MarketPickProcessor(store, factory),
            new ParticipantMarketPickProcessor(store),
        ];
    }

    processRequest(request: OptionRequest, picksView: PicksView, betBuilderFixtureId: string[], existingFixtureIds: string[]) {
        let optionQueryList: OptionQuery[] = [];
        optionQueryList = request.query;

        //comparing existings picks in the betslip and removing duplicate picks from the request.
        existingFixtureIds.forEach((id) => {
            const index = request.query.findIndex((p) => p.fixture === id);
            if (index !== -1 && betBuilderFixtureId.some((l: any) => l.optionId === request.query[index]?.option)) {
                optionQueryList.splice(index, 1);
            }
        });

        let failedCounter = 0;

        optionQueryList.forEach((pick) => {
            // legacy deep links where the fixture id is not available
            const fixture = picksView.fixturePage.fixtures.find(
                (current: Fixture) =>
                    ('source' in current &&
                        (this.hasGameOptions(pick, current.games) ||
                            this.hasMarketOptions(pick, current.optionMarkets) ||
                            this.hasParticipants(pick, current.participants))) ||
                    this.hasGroupPicks(pick, current),
            );

            if (fixture) {
                if (this.isGroupedPick(pick)) {
                    this.processGroupPick(fixture, pick, request, picksView);
                } else {
                    this.processSinglePick(fixture, pick, request, picksView);
                }
            } else {
                failedCounter++;
            }
        });

        if (failedCounter === optionQueryList.length) {
            this.store.dispatch(AffiliateActions.allPicksFailed());
        }
    }

    private processSinglePick(fixture: Fixture, pick: OptionQuery, request: OptionRequest, picksView: PicksView): void {
        const liveFallback = picksView.liveFallbacks[pick.option!];
        const option = liveFallback ? liveFallback.resultId : pick.option;
        let priceId: number | undefined;

        if (pick.priceId) {
            priceId = pick.priceId;
        } else if (fixture.source === OfferSource.V2) {
            const optionGroup = fixture.optionMarkets.find((om: OptionMarket) => om.options.some((o: Option) => o.id === pick.option));
            if (optionGroup === undefined) {
                throw new Error('Option group could not be found...');
            }
            const fixtureOption = optionGroup.options.find((o: Option) => o.id === pick.option);
            if (fixtureOption === undefined) {
                throw new Error('Option could not be found...');
            }
            priceId = fixtureOption.price.id;
            if (priceId === undefined) {
                throw new Error('PriceId could not be found...');
            }
        }

        let preCreatedFixture: Fixture | undefined;
        if (fixture?.source === OfferSource.BetBuilder) {
            preCreatedFixture = fixture;
            fixture = picksView.betBuilderSourceFixtures[preCreatedFixture.id];
        }

        this.process({
            id: option!,
            fixture,
            source: request.source,
            preCreatedFixture,
            optionGroupId: pick.optionGroup,
            priceId,
        });
    }

    private processGroupPick(fixture: Fixture, pick: OptionQuery, request: OptionRequest, picksView: PicksView): void {
        const referenceLeg = pick.tv1Legs?.length ? pick.tv1Legs[0] : pick.tv2Legs![0];
        if (!referenceLeg?.pickGroupId) {
            throw new Error(`Can not find reference leg...}`);
        }

        const priceId = picksView.betBuilderPricingGroups[referenceLeg.pickGroupId]?.odds?.odds;

        if (!priceId) {
            throw new Error(`Can not find any price group for ${referenceLeg.fixtureId}`);
        }

        // handle tv1 livefallback
        if (pick.tv1Legs?.length) {
            pick.tv1Legs.forEach((leg, index, legs) => {
                legs[index].resultId = leg.useLiveFallback ? picksView.liveFallbacks[legs[index].resultId!].resultId : legs[index].resultId;
            });
        }

        this.process({
            id: referenceLeg.pickGroupId,
            fixture,
            source: request.source,
            optionGroupId: pick.optionGroup,
            priceId,
            tv1Legs: pick.tv1Legs,
            tv2Legs: pick.tv2Legs,
        });
    }

    private process(pickOption: PickProcessorOption): void {
        for (const processor of this.processors) {
            if (processor.canProcess(pickOption.preCreatedFixture ?? pickOption.fixture)) {
                processor.process(pickOption);
            }
        }
    }

    private hasGameOptions = (pick: OptionQuery, games?: Game[]) =>
        !!games && games.some((g: Game) => g.results.some((r: Result) => r.id === pick.option));

    private hasMarketOptions = (pick: OptionQuery, markets?: OptionMarket[]) =>
        !!markets && markets.some((g: OptionMarket) => g.options.some((r: Option) => r.id === pick.option));

    private hasParticipants = (pick: OptionQuery, participants?: Participant[]) =>
        !!participants && !!pick.option && participants.some((p: Participant) => p.id === pick.option);

    private hasGroupPicks = (pick: OptionQuery, fixture: Fixture) => {
        let tv1OptionGroups = fixture.games.some((g: Game) =>
            pick.tv1Legs?.some((leg: Tv1PickData) => g.results.find((r: Result) => r.id === leg.resultId)),
        );
        const tv2OptionGroups = fixture.optionMarkets.some((om: OptionMarket) =>
            pick.tv2Legs?.some((leg: Tv2PickData) => om.options.find((o: Option) => o.id === leg.optionId)),
        );

        if (!tv1OptionGroups && !tv2OptionGroups && fixture.addons.betBuilderProvider === BetBuilderProvider.Angstrom) {
            tv1OptionGroups = fixture.optionMarkets.some((om: OptionMarket) =>
                pick.tv1Legs?.some((leg: Tv1PickData) => om.options.find((o: Option) => o.id === leg.resultId)),
            );
        }

        return tv1OptionGroups || tv2OptionGroups;
    };

    private isGroupedPick = (pick: OptionQuery): boolean => !!(pick.tv1Legs?.length || pick.tv2Legs?.length);
}
